import { useEffect, useRef, useState } from "react";
import {
	Animated,
	type LayoutChangeEvent,
	StyleSheet,
	View,
} from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { Text, useTheme } from "react-native-paper";
import { interpolateIntegerValue } from "./helpers";

type RangeSliderProps = {
	min: number;
	max: number;
	disabled?: boolean;
	value?: number[];
	onValueChange: (value: number[]) => void;
};

const THUMB_SIZE = 44;
const TRACK_HEIGHT = 16;

export const RangeSlider = ({
	min,
	max,
	value = [0, 10],
	disabled = false,
	onValueChange,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: RangeSliderProps) => {
	const { colors } = useTheme();
	const { current: rightPosition } = useRef(new Animated.Value(0));
	const { current: leftPosition } = useRef(new Animated.Value(0));
	const { current: activeTrackWidth } = useRef(new Animated.Value(0));
	const [sliderWidth, setSliderWidth] = useState(0);
	const [currentRightPosition, setCurrentRightPosition] = useState(0);
	const [currentLeftPosition, setCurrentLeftPosition] = useState(0);
	const [sliderValue, setSliderValue] = useState(value);
	const [isDragging, setIsDragging] = useState(false);
	const handleLayout = (event: LayoutChangeEvent) => {
		const { width } = event.nativeEvent.layout;
		setSliderWidth(width);
	};

	useEffect(() => {
		if (value) {
			const posLeft = interpolateIntegerValue(
				value[0],
				[min, max],
				[0, sliderWidth],
			);
			const posRight = interpolateIntegerValue(
				value[1],
				[min, max],
				[0, sliderWidth],
			);
			rightPosition.setValue(posRight);
			setCurrentRightPosition(posRight);
			leftPosition.setValue(posLeft);
			setCurrentLeftPosition(posLeft);
			activeTrackWidth.setValue(posRight - 16 - posLeft);
			setSliderValue(value);
		}
	}, [value, min, max, sliderWidth]);

	const panGestureLeft = Gesture.Pan()
		.onBegin(() => {
			setIsDragging(true);
			leftPosition.addListener(({ value }) => {
				const ratio = value / sliderWidth;
				const interpolatedValue = Math.round(min + ratio * (max - min));
				const val = Math.min(Math.max(interpolatedValue, min), max);
				setSliderValue([val, sliderValue[1]]);
			});
		})
		.onUpdate((e) => {
			const newPosition = Math.max(
				0,
				Math.min(e.translationX + currentLeftPosition, currentRightPosition),
			);
			leftPosition.setValue(newPosition);
			activeTrackWidth.setValue(currentRightPosition - 16 - newPosition);
		})
		.onEnd((e) => {
			setCurrentLeftPosition(e.translationX + currentLeftPosition);
			setIsDragging(false);
			onValueChange(sliderValue ?? 0);
		})
		.runOnJS(true);

	const panGestureRight = Gesture.Pan()
		.onBegin(() => {
			setIsDragging(true);
			rightPosition.addListener(({ value }) => {
				const ratio = value / sliderWidth;
				const interpolatedValue = Math.round(min + ratio * (max - min));
				const val = Math.min(Math.max(interpolatedValue, min), max);
				setSliderValue([sliderValue[0], val]);
			});
		})
		.onUpdate((e) => {
			const newPosition = Math.max(
				currentLeftPosition,
				Math.min(e.translationX + currentRightPosition, sliderWidth),
			);
			rightPosition.setValue(newPosition);
			activeTrackWidth.setValue(newPosition - 16 - currentLeftPosition);
		})
		.onEnd((e) => {
			setCurrentRightPosition(e.translationX + currentRightPosition);
			setIsDragging(false);
			onValueChange(sliderValue ?? 0);
		})
		.runOnJS(true);

	return (
		<View
			pointerEvents={disabled ? "none" : "auto"}
			style={[styles.track]}
			onLayout={handleLayout}
		>
			<Animated.View
				style={[
					styles.trackInactive,
					styles.trackInactiveLeft,
					{
						width: leftPosition.interpolate({
							inputRange: [0, sliderWidth],
							outputRange: ["0%", "100%"],
							extrapolate: "clamp",
						}),
						backgroundColor: disabled
							? colors.onSurface
							: colors.secondaryContainer,
						opacity: disabled ? 0.12 : 1,
					},
				]}
			/>
			<GestureDetector gesture={panGestureLeft}>
				<Animated.View
					style={[styles.thumb, { transform: [{ translateX: leftPosition }] }]}
				>
					<View
						style={{
							width: isDragging ? 2 : 4,
							height: "100%",
							backgroundColor: disabled ? colors.onSurface : colors.primary,
							opacity: disabled ? 0.38 : 1,
							borderRadius: 4,
						}}
					/>
					{isDragging && (
						<Text
							style={[
								styles.valueLabel,
								{
									opacity: disabled ? 0.12 : 1,
									backgroundColor: colors.inverseSurface,
									color: colors.inverseOnSurface,
								},
							]}
						>
							{sliderValue[0]}
						</Text>
					)}
				</Animated.View>
			</GestureDetector>
			<Animated.View
				style={[
					styles.trackActive,
					{
						width: activeTrackWidth.interpolate({
							inputRange: [0, sliderWidth],
							outputRange: [0, sliderWidth],
							extrapolate: "clamp",
						}),
						backgroundColor: disabled ? colors.onSurface : colors.primary,
						opacity: disabled ? 0.38 : 1,
					},
				]}
			/>
			<GestureDetector gesture={panGestureRight}>
				<Animated.View
					style={[styles.thumb, { transform: [{ translateX: rightPosition }] }]}
				>
					<View
						style={{
							width: isDragging ? 2 : 4,
							height: "100%",
							backgroundColor: disabled ? colors.onSurface : colors.primary,
							opacity: disabled ? 0.38 : 1,
							borderRadius: 4,
						}}
					/>
					{isDragging && (
						<Text
							style={[
								styles.valueLabel,
								{
									opacity: disabled ? 0.12 : 1,
									backgroundColor: colors.inverseSurface,
									color: colors.inverseOnSurface,
								},
							]}
						>
							{sliderValue[1]}
						</Text>
					)}
				</Animated.View>
			</GestureDetector>
			<Animated.View
				style={[
					styles.trackInactive,
					{
						width: rightPosition.interpolate({
							inputRange: [0, sliderWidth],
							outputRange: ["100%", "0%"],
							extrapolate: "clamp",
						}),
						backgroundColor: disabled
							? colors.onSurface
							: colors.secondaryContainer,
						opacity: disabled ? 0.12 : 1,
					},
				]}
			>
				<View
					style={{
						right: 6,
						width: 4,
						height: 4,
						backgroundColor: disabled ? colors.onSurface : colors.primary,
						opacity: disabled ? 0.38 : 1,
						borderRadius: 2,
					}}
				/>
			</Animated.View>
		</View>
	);
};

const styles = StyleSheet.create({
	track: {
		width: "100%",
		flexDirection: "row",
		height: TRACK_HEIGHT,
		borderRadius: TRACK_HEIGHT / 2,
		gap: 16,
		marginHorizontal: THUMB_SIZE / 2,
	},
	trackActive: {
		height: TRACK_HEIGHT,
		borderRadius: 2,
	},
	trackInactive: {
		height: TRACK_HEIGHT,
		borderTopRightRadius: TRACK_HEIGHT / 2,
		borderBottomRightRadius: TRACK_HEIGHT / 2,
		borderTopLeftRadius: 2,
		borderBottomLeftRadius: 2,
		alignItems: "flex-end",
		justifyContent: "center",
	},
	trackInactiveLeft: {
		borderTopRightRadius: 2,
		borderBottomRightRadius: 2,
		borderTopLeftRadius: TRACK_HEIGHT / 2,
		borderBottomLeftRadius: TRACK_HEIGHT / 2,
	},
	thumb: {
		position: "absolute",
		width: 16,
		height: THUMB_SIZE,
		alignItems: "center",
		borderRadius: 6,
		top: -(THUMB_SIZE - TRACK_HEIGHT) / 2,
		left: 0,
		zIndex: 2,
	},
	valueLabel: {
		position: "absolute",
		bottom: THUMB_SIZE + 4,
		minWidth: 48,
		textAlign: "center",
		padding: 12,
		borderRadius: 100,
	},
});
